/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { margin, padding } from 'polished';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import PayoutIndicator from 'app/components/commons/PayoutIndicator';
import TicketIndicator from 'app/components/commons/TicketIndicator';
import Tooltip from 'app/components/commons/Tooltip';
import { displayPrice } from 'app/utils/priceUtils';
import { IN_PROGRESS } from '../../constants/challengeStatusTypes';
import Icon from '../commons/Icon';

type Props = {
  actualPrice: number,
  entryCap: number,
  entryCount: number,
  isTicketChallenge: boolean,
  isUnlimited: boolean,
  originalPrice: number,
  maxEntriesPerPerson: number,
  payout: string,
  payoutMessage: string,
  payoutIcon: string,
  prizePool: string,
  statusId: number
};

const Wrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  [theme.mediaQuery.tabletPortraitAndUp]: {
    ...margin(null, -theme.spacing.small)
  },
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: theme.fontSize.xsmall,
    justifyContent: 'center',
    ...margin(null, '-2.5rem')
  },
  [theme.mediaQuery.desktopLargeOnly]: {
    ...margin(null, '-1rem'),
    paddingTop: 0
  }
}));

const SecondaryItem = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexBasis: '50%',
  flexDirection: 'column',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'space-between',
  paddingTop: theme.spacing.medium,
  [theme.mediaQuery.tabletPortraitAndUp]: {
    flexBasis: 'auto',
    ...padding(null, theme.spacing.small)
  },
  [theme.mediaQuery.desktopAndUp]: {
    ...padding(null, '2.5rem')
  },
  [theme.mediaQuery.desktopLargeOnly]: {
    alignItems: 'flex-start',
    ...padding(0, '1rem', null)
  }
}));

const SecondaryTitle = styled.span(({ theme }) => ({
  alignItems: 'center',
  color: theme.color.basis5,
  display: 'flex',
  fontSize: theme.fontSize.small,
  marginBottom: 3,
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: theme.fontSize.xsmall
  },
  [theme.mediaQuery.desktopLargeOnly]: {
    marginTop: theme.spacing.xsmall
  }
}));

const SecondaryContent = styled.span(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  fontWeight: theme.fontWeight.demiBold
}));

const IconWrapper = styled.span({
  cursor: 'pointer',
  display: 'flex',
  paddingLeft: '0.25em',
  transform: 'translateY(-1px)'
});

const OriginalPrice = styled.span({
  paddingRight: '0.5em',
  textDecoration: 'line-through'
});

function ChallengeOverviewSecondaryInfo({
  actualPrice,
  entryCap,
  entryCount,
  isTicketChallenge,
  isUnlimited,
  maxEntriesPerPerson,
  originalPrice,
  payout,
  payoutIcon,
  payoutMessage,
  prizePool,
  statusId
}: Props) {
  return (
    <Wrapper>
      <SecondaryItem>
        <SecondaryTitle>Entry Cost</SecondaryTitle>
        <SecondaryContent>
          {actualPrice > 0 && <CurrencyIcon />}
          {originalPrice !== actualPrice && (
            <OriginalPrice>{displayPrice(originalPrice)}</OriginalPrice>
          )}
          {displayPrice(actualPrice)}
        </SecondaryContent>
      </SecondaryItem>
      <SecondaryItem>
        <SecondaryTitle>Max Entries</SecondaryTitle>
        <SecondaryContent>{maxEntriesPerPerson} pp</SecondaryContent>
      </SecondaryItem>
      <SecondaryItem>
        <SecondaryTitle>
          Entries
          {maxEntriesPerPerson > 1 && (
            <Tooltip title="Multi Entry">
              <IconWrapper>
                <Icon type="icon-multientry" />
              </IconWrapper>
            </Tooltip>
          )}
        </SecondaryTitle>
        <SecondaryContent>
          {entryCount}/{entryCap}
          {isUnlimited && statusId < IN_PROGRESS ? '+' : ''}
        </SecondaryContent>
      </SecondaryItem>
      <SecondaryItem>
        <SecondaryTitle>
          Payout
          {!isTicketChallenge && (
            <Tooltip title={payoutMessage}>
              <IconWrapper>
                <PayoutIndicator src={payoutIcon} />
              </IconWrapper>
            </Tooltip>
          )}
        </SecondaryTitle>
        <SecondaryContent>{payout}</SecondaryContent>
      </SecondaryItem>
      <SecondaryItem>
        <SecondaryTitle>Prize</SecondaryTitle>
        <SecondaryContent>
          {isTicketChallenge ? (
            <Tooltip title={payoutMessage}>
              <TicketIndicator src={payoutIcon} />
            </Tooltip>
          ) : (
            <React.Fragment>
              <CurrencyIcon />
              {prizePool}
            </React.Fragment>
          )}
        </SecondaryContent>
      </SecondaryItem>
    </Wrapper>
  );
}

function mapStateToProps(state) {
  return {
    actualPrice: state.challenge.reducedCost,
    entryCap: state.challenge.entryCap,
    entryCount: state.challenge.entryCount,
    isTicketChallenge: state.challenge.isTicketChallenge,
    isUnlimited: state.challenge.isUnlimited,
    maxEntriesPerPerson: state.challenge.maxEntriesPerPerson,
    originalPrice: state.challenge.cost,
    payout: state.challenge.payout,
    payoutIcon: state.challenge.payoutIcon,
    payoutMessage: state.challenge.payoutMessage,
    prizePool: state.challenge.prizePool,
    statusId: state.challenge.statusId
  };
}

export default connect(mapStateToProps)(ChallengeOverviewSecondaryInfo);
